import { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Upload, X } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { CLINIC_LOGO_UPDATED } from "@/lib/clinicLogoEvents";
import QRCropper from "@/components/settings/QRCropper";

const LOGO_BUCKET = "clinic-logos";
const LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

export default function LogoUploader({
  clinicId,
  clinicName,
  logoUrl,
  onChange,
  showNamePreview = false,
  testIdPrefix = "logo",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  function handleFilePicked(e) {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!LOGO_MIME_TYPES.includes(file.type)) {
      toast.error("Logo must be PNG, JPEG, or WEBP.");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error("Logo must be 2 MB or smaller.");
      return;
    }
    setPendingFile(file);
  }

  async function handleCropped(blob) {
    setPendingFile(null);
    if (!clinicId) {
      toast.error("No active clinic.");
      return;
    }
    setUploading(true);
    const objectPath = `${clinicId}/logo-${Date.now()}.png`;
    const { error: uploadErr } = await supabase.storage
      .from(LOGO_BUCKET)
      .upload(objectPath, blob, { contentType: "image/png", upsert: false });
    if (uploadErr) {
      setUploading(false);
      toast.error(uploadErr.message ?? "Could not upload logo.");
      return;
    }
    const { data: urlData } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(objectPath);
    const publicUrl = urlData?.publicUrl;
    if (!publicUrl) {
      setUploading(false);
      toast.error("Logo uploaded but couldn't read its URL.");
      return;
    }
    const { error: rpcErr } = await supabase.rpc("update_clinic_settings", {
      p_input: { logo_url: publicUrl },
    });
    setUploading(false);
    if (rpcErr) {
      toast.error(rpcErr.message ?? "Logo uploaded but couldn't save URL.");
      return;
    }
    onChange?.(publicUrl);
    window.dispatchEvent(new CustomEvent(CLINIC_LOGO_UPDATED));
    toast.success("Logo updated.");
  }

  async function handleClear() {
    if (!logoUrl) return;
    if (!window.confirm("Remove the clinic logo? PDFs will fall back to the default header.")) return;
    setUploading(true);
    const { error } = await supabase.rpc("update_clinic_settings", {
      p_input: { logo_url: "" },
    });
    setUploading(false);
    if (error) {
      toast.error(error.message ?? "Could not clear logo.");
      return;
    }
    onChange?.("");
    window.dispatchEvent(new CustomEvent(CLINIC_LOGO_UPDATED));
    toast.success("Logo cleared.");
  }

  return (
    <div className="flex flex-wrap items-center gap-5" data-testid={`${testIdPrefix}-uploader`}>
      <div
        className="w-28 h-28 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0"
        data-testid={`${testIdPrefix}-preview`}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="Clinic logo" className="w-full h-full object-contain" />
        ) : (
          <ImageIcon className="w-8 h-8 text-slate-300" />
        )}
      </div>

      {showNamePreview ? (
        <div className="min-w-0 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 overflow-hidden flex items-center justify-center">
            {logoUrl ? <img src={logoUrl} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
          </div>
          <p className="truncate text-sm font-semibold text-slate-900">{clinicName || "Your clinic"}</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <input
          ref={fileRef}
          type="file"
          accept={LOGO_MIME_TYPES.join(",")}
          onChange={handleFilePicked}
          className="sr-only"
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload`}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {logoUrl ? "Replace logo" : "Upload logo"}
        </button>
        {logoUrl ? (
          <button
            type="button"
            onClick={handleClear}
            disabled={uploading}
            data-testid={`${testIdPrefix}-clear`}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-lg disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" /> Remove
          </button>
        ) : null}
      </div>

      {pendingFile ? (
        <QRCropper
          file={pendingFile}
          title="Crop logo"
          description="Drag to position. Crop is locked to a square."
          testId={`${testIdPrefix}-cropper`}
          onCancel={() => setPendingFile(null)}
          onCropped={handleCropped}
        />
      ) : null}
    </div>
  );
}
